import React, { FC, useContext, useRef, useState } from 'react';
import { ExtendedRenewalOverview, ExtendedRenewalOverviewUsageLimitValue } from '../../utils/renewalTableTileTypes';
import { Link } from 'react-router-dom';
import MaterialIcon from '../../../../shared/common/MaterialIcon';
import { SelectionContext } from '../../context/SelectionContext';
import { ProductCoverCell } from '../ProductCoverCell';
import { IssueUsageStatisticsCell } from '../../../../shared/subscription-management/IssueUsageStatisticsCell';
import { ExtendedManageLineUsageLimit } from '../../../../shared/subscription-management/types/ExtendedManageLineShared';
import { ProductCopyStockDropdown } from '../../../../shared/product/ProductCopyStock';
import { useTranslation } from 'react-i18next';
import { LOANFORMAT_EPRESS_OBJECT } from '../../../../shared/loanFormat/constants';

type Props = ExtendedRenewalOverview & {
    copiesListOpen: boolean;
    toggleCopiesList: () => void;
    userSubscribedLabel?: string;
    showWarning?: 'orange' | 'red';
};

export const ExtendedRenewalOverviewRow: FC<Props> = props => {
    const { t } = useTranslation();
    const {
        product,
        copiesListOpen,
        toggleCopiesList,
        currentYearSubscription,
        nextYearSubscription,
        previousIssueUsage,
        currentIssueUsage,
        userSubscribedLabel,
        showWarning
    } = props;
    const { title } = product;
    const { isInSelectionMode, isSelectAll } = useContext(SelectionContext);

    const productLink = !isInSelectionMode ? `/products/${product.id}` : '#';

    return (
        <tr className="overview-row">
            <ProductCoverCell product={product} isInSelectionMode={isInSelectionMode} isSelectAll={isSelectAll} />
            <td className="product-title-cell">
                <div>
                    <div>
                        <Link to={productLink}>
                            <b>{title}</b>
                        </Link>
                        {showWarning ? <MaterialIcon icon="warning" className={showWarning} /> : null}
                    </div>
                    <small>{userSubscribedLabel ? userSubscribedLabel : null}</small>
                </div>
                <button onClick={toggleCopiesList}>
                    {t('shop.subscriptions.showCopies')}
                    <MaterialIcon icon={copiesListOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} />
                </button>
            </td>
            <td className="current-year-subscriptions">
                {(() => {
                    switch (currentYearSubscription.type) {
                        case ExtendedManageLineUsageLimit.Value:
                            return <ExtendedUsageLimitValueView {...currentYearSubscription} />;
                        case ExtendedManageLineUsageLimit.None:
                            return (
                                <>
                                    <small>{currentYearSubscription.label}</small>
                                    <div>
                                        (0 + <MaterialIcon icon="shared" primary={false} className="shared-icon" />
                                        0)
                                    </div>
                                </>
                            );
                        default:
                            return <></>;
                    }
                })()}
            </td>
            <td className="previous-issue-usage">
                {previousIssueUsage ? <IssueUsageStatisticsCell {...previousIssueUsage} /> : null}
            </td>
            <td className="current-issue-usage">
                {currentIssueUsage ? <IssueUsageStatisticsCell {...currentIssueUsage} /> : null}
            </td>
            <td className="next-year-subscriptions">
                {(() => {
                    switch (nextYearSubscription.type) {
                        case ExtendedManageLineUsageLimit.Value:
                            return <ExtendedUsageLimitValueView {...nextYearSubscription} />;
                        case ExtendedManageLineUsageLimit.None:
                            return <p>{nextYearSubscription.label}</p>;
                        default:
                            return <></>;
                    }
                })()}
            </td>
        </tr>
    );
};

const ExtendedUsageLimitValueView: FC<ExtendedRenewalOverviewUsageLimitValue> = props => {
    const ref = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);

    return (
        <>
            <div ref={ref} onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
                <b>{props.overviewLabel}</b>
                <div>
                    ({props.individuellLabel} + <MaterialIcon icon="shared" primary={false} className="shared-icon" />
                    {props.sharedLabel})
                </div>
            </div>
            <ProductCopyStockDropdown
                loanFormat={LOANFORMAT_EPRESS_OBJECT}
                stockDetails={props.stockDetails}
                placement="top-start"
                visible={visible}
                popoverRef={ref}
            />
        </>
    );
};
